﻿namespace eXpress.core {
	export interface ISelectPhotoAlbumOptions {
		itemCssClass: string;
		containerOpenCssClass: string;
		readonly scrollContainerEl?: HTMLElement;
		readonly scrollContainerHeight?: () => number;
	}

	export interface ISelectPhotoAlbumDependencies {
		contentManager: ContentManager;
		storageService: services.StorageService;
	}

	export class SelectPhotoAlbum implements IPanel<IPhotoFolder | undefined> {
		private readonly options: ISelectPhotoAlbumOptions;
		private readonly contentManager: ContentManager;

		private readonly containerEl: HTMLElement;
		private readonly placeholderEl: HTMLElement;

		private $deferred: JQueryDeferred<IPhotoFolder | undefined>;

		private folders: IPhotoFolder[] | null;

		private eventsDisposable?: IDisposable;
		private showAlbum: boolean;

		constructor(containerEl: HTMLElement, deps: ISelectPhotoAlbumDependencies, options: ISelectPhotoAlbumOptions) {
			this.options = options;

			this.contentManager = deps.contentManager;

			this.containerEl = containerEl;
			this.placeholderEl = eXpress.html.hasClass(containerEl, "placeholder") ? containerEl : containerEl.querySelector('.placeholder') as HTMLElement;
			if (!this.placeholderEl)
				throw new Error("The containerEl must have a class 'placeholder' or must container a child element with a class 'placeholder'");
		}

		private init() {
			const eventListeners = [
				html.addEventListener(this.placeholderEl, 'click', e => {
					const targetEl = e.target as HTMLElement;
					const albumEl = targetEl.closest('.' + this.options.itemCssClass);
					if (albumEl && this.folders) {
						const folderCodeAttribute = albumEl.getAttribute("data-folder-id");
						this.close(utils.findFirst(this.folders, f => f.id === folderCodeAttribute));
					}
				})
			];

			if (this.options.scrollContainerHeight && this.options.scrollContainerEl) {
				eventListeners.push(
					html.addEventListener(window, "resize", this.setScrollContainerHeight.bind(this)),
					html.addEventListener(window, "orientationchange", this.setScrollContainerHeight.bind(this))
				);
			}

			this.eventsDisposable = utils.compositeDisposable(eventListeners);
		}

		private deinit() {
			if (this.eventsDisposable) this.eventsDisposable.dispose();
		}

		public close(value: IPhotoFolder | undefined) {
			html.removeClass(this.containerEl, this.options.containerOpenCssClass);
			this.deinit();
			this.$deferred.resolve(value);
		}

		public hasFolders = () => this.folders;

		public createAsync(store: IPhotoStore): JQueryPromise<void> {
			this.showAlbum = store.showAlbum;

			// todo: use labels from options 
			const pLabels = this.contentManager.getLabelsAsync(
				[
					"Label.MyPages.Main.MyAlbums.Overview",
					"Label.MyPages.MyAlbums.NumberOfPhotos",
					"Label.ResponsivePrintForm.Import.Import",
					"Label.ResponsivePrintForm.Import.NoAlbums",
					"Label.ResponsivePrintForm.Import.Error"
				]);

			const pFolders = store.getFoldersAsync()
				.then(folders => this.folders = !folders ? null : folders.filter(folder => folder.fileCount !== 0));

			return $.when<any>(pLabels, pFolders).then(() => {
				return;
			});
		}

		public openAsync<PhotoFolder>() {
			html.addClass(this.containerEl, this.options.containerOpenCssClass);

			this.$deferred = $.Deferred<IPhotoFolder | undefined>();
			const renderedFolderEls = this.placeholderEl.querySelectorAll('.' + this.options.itemCssClass);

			if (!this.folders) {
				this.placeholderEl.insertAdjacentHTML("beforeend", `<div class="erorr">${this.contentManager.getLabel("Label.ResponsivePrintForm.Import.Error")}</div>`);
				const tokenexpiry = html.querySelector(document, '.technical-tokenexpiry', false);
				if (tokenexpiry) tokenexpiry.remove(); // remove token expiry => no reason to show it when no albums
				return this.$deferred;
			}

			if (!this.showAlbum && this.folders[0]) {
				this.close(this.folders[0]);
				return this.$deferred;
			}
			// remove previous rendered folders
			for (let i = 0; i < renderedFolderEls.length; i++) {
				const itemEl = renderedFolderEls.item(i);
				html.remove(itemEl);
			}

			if (this.folders.length > 0) {
				// add new rendered folders to container
				const folderCoversHtml = this.folders.map(folder => this.getFolderCoverTemplate(folder)).join('');
				this.placeholderEl.insertAdjacentHTML("beforeend", folderCoversHtml);
			}
			else {
				this.placeholderEl.insertAdjacentHTML("beforeend", `<div class="no-albums">${this.contentManager.getLabel("Label.ResponsivePrintForm.Import.NoAlbums")}</div>`);
			}

			this.setScrollContainerHeight();
			this.init();


			return this.$deferred;
		}

		private setScrollContainerHeight() {
			if (!this.options.scrollContainerEl || !this.options.scrollContainerHeight) return;
			this.options.scrollContainerEl.style.height = this.options.scrollContainerHeight() + "px";
		}

		private getFolderCoverTemplate(folder: IPhotoFolder): string {
			let nrOfPhotos = this.contentManager.getLabel("Label.MyPages.MyAlbums.NumberOfPhotos");
			nrOfPhotos = utils.formatString(nrOfPhotos, folder.fileCount.toString());

			let meta = '';
			if (folder.dateCreated && folder.dateCreated > new Date(1970, 1) && folder.dateCreated <= new Date())
				meta = folder.dateCreated.toLocaleDateString();
			if (folder.fileCount >= 0) {
				if (meta)
					meta += " - ";
				meta += nrOfPhotos;
			}

			return `<div class="item ${this.options.itemCssClass}" data-folder-id="${folder.id}">
					<div class="item-container ${!folder.mediumUrl ? `empty` : ""}">
						<div class="title">
							<span class="name">${folder.name}</span>
							<span class="meta">${meta}</span>
						</div>
						${folder.mediumUrl ? `<img src="${folder.mediumUrl}">` : ""}
					</div>
				</div>`;
		}
	}
}
